import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { eventsApi } from '../api/events';
import { getErrorMessage } from '../api/client';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';
import ShareBox from '../components/ShareBox';
import Input from '../components/ui/Input';
import DynamicForm from '../components/DynamicForm';
import GalleryUploader from '../components/GalleryUploader';
import AudioUploader from '../components/AudioUploader';
import CoverUploader from '../components/CoverUploader';

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editData, setEditData] = useState({});
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const ev = await eventsApi.get(id);
      setEvent(ev);
      setEditTitle(ev.title);
      setEditData(ev.data || {});
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const handlePublish = async () => {
    setPublishing(true);
    setError('');
    try {
      const ev = await eventsApi.publish(id);
      setEvent((prev) => ({ ...prev, ...ev }));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const ev = await eventsApi.update(id, { title: editTitle, data: editData });
      setEvent((prev) => ({ ...prev, ...ev }));
      setEditing(false);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner size="lg" className="text-brand-500" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500 mb-4">{error || 'Evento no encontrado'}</p>
        <Link to="/dashboard" className="font-semibold text-brand-600 hover:underline">
          Volver al dashboard
        </Link>
      </div>
    );
  }

  const isActive = event.status === 'ACTIVE';
  const isDraft = event.status === 'DRAFT';
  const isExpired = event.status === 'EXPIRED';

  return (
    <div className="space-y-6">
      <Link to="/dashboard" className="text-xs text-slate-500 hover:text-brand-600">
        ← Volver a mis eventos
      </Link>

      {/* ============================ HEADER ============================ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">{event.title}</h1>
          <p className="text-sm text-slate-500">
            {event.template?.name} — {event.template?.category}
          </p>
        </div>
        {isDraft && (
          <Button onClick={handlePublish} disabled={publishing}>
            {publishing ? 'Publicando...' : 'Publicar evento'}
          </Button>
        )}
      </div>

      {/* ============================ ALERTS ============================ */}
      {error && <Alert type="error">{error}</Alert>}
      {isActive && (
        <Alert type="success">
          Tu evento está activo. Expira el{' '}
          {new Date(event.expiresAt).toLocaleDateString('es-PE', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })}
          .
        </Alert>
      )}
      {isExpired && (
        <Alert type="warning">
          Este evento expiró. Reactívalo pagando un plan para seguir compartiéndolo.
        </Alert>
      )}

      {/* ============================ LINK PÚBLICO ============================ */}
      {isActive && <ShareBox shortCode={event.shortCode} title={event.title} />}

      {/* ============================ DATOS + DETALLES ============================ */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Datos del evento */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">Datos del evento</h2>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="text-xs font-semibold text-brand-600 hover:underline"
              >
                Editar
              </button>
            )}
          </div>

          {editing ? (
            <>
              <Input
                label="Título interno"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
              <DynamicForm
                schema={event.template?.schema}
                values={editData}
                onChange={setEditData}
              />
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveEdit} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(false);
                    load();
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </>
          ) : (
            <dl className="grid sm:grid-cols-2 gap-4">
              {(event.template?.schema?.fields || []).map((f) => (
                <div key={f.key}>
                  <dt className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
                    {f.label}
                  </dt>
                  <dd className="text-sm text-slate-700 mt-1 break-words">
                    {event.data?.[f.key] || <span className="text-slate-300">—</span>}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Detalles */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3 text-sm">
          <h2 className="font-bold">Detalles</h2>

          <div className="flex justify-between text-slate-500">
            <span>Estado</span>
            <span className="font-semibold text-slate-700">{event.status}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>Plan</span>
            <span className="font-semibold text-slate-700">{event.planCode}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>Short code</span>
            <span className="font-mono text-xs text-slate-700">{event.shortCode}</span>
          </div>
          {event.publishedAt && (
            <div className="flex justify-between text-slate-500">
              <span>Publicado</span>
              <span className="font-semibold text-slate-700">
                {new Date(event.publishedAt).toLocaleDateString('es-PE')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ============================ FOTO DE PORTADA ============================ */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <CoverUploader eventId={event.id} />
      </div>

      {/* ============================ GALERÍA DE FOTOS ============================ */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <GalleryUploader eventId={event.id} max={6} />
      </div>

      {/* ============================ MÚSICA DE FONDO ============================ */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <AudioUploader eventId={event.id} />
      </div>
    </div>
  );
}